import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { litScholarAPI } from '../api';
import { toast } from 'sonner';
import {
  Loader2,
  MessageSquare,
  FileText,
  GitCompareArrows,
  Send,
  AlertTriangle,
  BookOpen,
  ArrowLeft,
  RefreshCw,
  User,
  Bookmark,
  PenLine,
  ExternalLink,
} from 'lucide-react';

export const LitScholarPanel = ({
  open,
  onOpenChange,
  article = null,
  selectedArticles = [],
}) => {
  const navigate = useNavigate();
  const isMultiMode = selectedArticles.length > 0;
  const pmids = isMultiMode
    ? selectedArticles.map((a) => a.pmid)
    : article
      ? [article.pmid]
      : [];

  const [surface, setSurface] = useState('idle');
  const [loading, setLoading] = useState(false);
  const [quotaError, setQuotaError] = useState(null);

  const [briefResult, setBriefResult] = useState(null);
  const [question, setQuestion] = useState('');
  const [answerResult, setAnswerResult] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [draftResult, setDraftResult] = useState(null);

  // Expertise profile state
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const rebuildInFlight = useRef(false);
  const lastRebuildTs = useRef(0);

  // Cached-first profile load with conditional background refresh
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadProfile = async () => {
      setProfileLoading(true);
      try {
        // Step 1: fast-path — fetch cached profile
        const state = await litScholarAPI.getProfile();
        if (cancelled) return;
        setProfile(state?.expertise_profile || null);
        setProfileLoading(false);

        // Step 2: decide if background rebuild is needed
        const rebuildTs = state?.last_rebuild_completed_at
          ? new Date(state.last_rebuild_completed_at).getTime()
          : 0;
        const ageMs = Date.now() - rebuildTs;
        const STALE_MS = 24 * 60 * 60 * 1000;     // 24 h
        const THROTTLE_MS = 12 * 60 * 60 * 1000;   // 12 h hard throttle
        const needsRebuild = rebuildTs === 0 || ageMs > STALE_MS;
        const throttled = (Date.now() - lastRebuildTs.current) < THROTTLE_MS;

        if (needsRebuild && !throttled && !rebuildInFlight.current) {
          rebuildInFlight.current = true;
          lastRebuildTs.current = Date.now();
          litScholarAPI.rebuildProfile()
            .then((fresh) => {
              if (!cancelled) setProfile(fresh?.expertise_profile || null);
            })
            .catch(() => { /* silent — cached profile remains */ })
            .finally(() => { rebuildInFlight.current = false; });
        }
      } catch {
        // getProfile failed — panel stays usable without personalization
        if (!cancelled) setProfileLoading(false);
      }
    };

    loadProfile();
    return () => { cancelled = true; };
  }, [open]);

  const resetState = () => {
    setSurface('idle');
    setBriefResult(null);
    setAnswerResult(null);
    setCompareResult(null);
    setDraftResult(null);
    setQuestion('');
    setQuotaError(null);
    setProfile(null);
  };

  const handleQuotaError = (d) => {
    setQuotaError({
      message: d.message || 'Daily limit reached.',
      retryAfter: d.retry_after_seconds || 3600,
    });
    toast.error('Lit Scholar limit reached for today.');
  };

  const handleEvidenceBrief = async () => {
    if (!pmids[0]) return;
    setSurface('evidence_brief');
    setLoading(true);
    setQuotaError(null);
    try {
      const result = await litScholarAPI.evidenceBrief(pmids[0]);
      setBriefResult(result);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error_code === 'copilot_quota_exceeded') handleQuotaError(d);
      else if (d?.error_code === 'copilot_disabled') toast.error('Lit Scholar is currently unavailable.');
      else if (d?.error_code === 'premium_required') toast.error('Pro subscription required.');
      else toast.error('Failed to generate evidence brief.');
    } finally {
      setLoading(false);
    }
  };

  const handleAskArticle = async () => {
    if (pmids.length === 0 || !question.trim()) return;
    setLoading(true);
    setQuotaError(null);
    try {
      const result = await litScholarAPI.askArticle(pmids, question.trim());
      setAnswerResult(result);
      setQuestion('');
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error_code === 'phi_detected') toast.error(d.message || 'PHI detected in question.');
      else if (d?.error_code === 'copilot_quota_exceeded') handleQuotaError(d);
      else if (d?.error_code === 'copilot_disabled') toast.error('Lit Scholar is currently unavailable.');
      else toast.error('Failed to get answer.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompareStudies = async () => {
    if (pmids.length < 2) {
      toast.error('Select 2-5 articles to compare.');
      return;
    }
    setSurface('compare_studies');
    setLoading(true);
    setQuotaError(null);
    try {
      const result = await litScholarAPI.compareStudies(pmids);
      setCompareResult(result);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error_code === 'copilot_quota_exceeded') handleQuotaError(d);
      else if (d?.error_code === 'copilot_disabled') toast.error('Lit Scholar is currently unavailable.');
      else if (d?.error_code === 'premium_required') toast.error('Pro subscription required.');
      else toast.error('Failed to compare studies.');
    } finally {
      setLoading(false);
    }
  };

  const handleDraftPost = async () => {
    if (pmids.length === 0) return;
    setSurface('draft_post');
    setLoading(true);
    setQuotaError(null);
    try {
      const result = await litScholarAPI.draftPost({
        specialty_id: profile?.specialty_ids?.[0] || 'general',
        pmids,
      });
      setDraftResult(result);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error_code === 'copilot_quota_exceeded') handleQuotaError(d);
      else if (d?.error_code === 'copilot_disabled') toast.error('Lit Scholar is currently unavailable.');
      else if (d?.error_code === 'premium_required') toast.error('Pro subscription required.');
      else toast.error('Failed to generate draft.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInCommunity = () => {
    if (!draftResult?.draft_post) return;
    // Store draft in sessionStorage for safe handoff (no URL length limits)
    sessionStorage.setItem('litscholar_draft', JSON.stringify({
      draft_post: draftResult.draft_post,
      pmids,
      specialty_id: profile?.specialty_ids?.[0] || '',
      suggested_questions: draftResult.suggested_questions || [],
    }));
    onOpenChange(false);
    navigate(`/community/new-thread?specialty=${encodeURIComponent(profile?.specialty_ids?.[0] || '')}&from=litscholar`);
  };

  const canCompare = pmids.length >= 2 && pmids.length <= 5;

  const prompts = [
    {
      id: 'evidence_brief',
      label: 'Develop Evidence Brief',
      icon: FileText,
      description: 'Generate a structured evidence summary from the article abstract.',
      disabled: pmids.length === 0,
      hidden: isMultiMode,
      action: handleEvidenceBrief,
    },
    {
      id: 'ask_article',
      label: 'Ask Article',
      icon: MessageSquare,
      description: 'Ask a question grounded in the article text.',
      disabled: pmids.length === 0,
      hidden: isMultiMode,
      action: () => setSurface('ask_article'),
    },
    {
      id: 'compare_studies',
      label: 'Compare Studies',
      icon: GitCompareArrows,
      description: `Compare ${pmids.length} selected article${pmids.length !== 1 ? 's' : ''} side-by-side.`,
      disabled: !canCompare,
      hidden: false,
      action: handleCompareStudies,
    },
    {
      id: 'draft_post',
      label: 'Draft Discussion Post',
      icon: PenLine,
      description: 'Generate a draft community post grounded in the selected articles.',
      disabled: pmids.length === 0,
      hidden: false,
      action: handleDraftPost,
    },
  ].filter((p) => !p.hidden);

  const renderQuotaWarning = () =>
    quotaError && (
      <div
        className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm"
        data-testid="litscholar-quota-error"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-amber-800 font-medium">{quotaError.message}</p>
            <p className="text-xs text-amber-600 mt-1">
              Try again in ~{Math.ceil(quotaError.retryAfter / 3600)} hour(s).
            </p>
          </div>
        </div>
      </div>
    );

  const renderCitations = (citations, sanitized, warning) => (
    <>
      {citations?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {citations.map((c, i) => (
            <a
              key={i}
              href={`/article/${c.pmid}`}
              className="text-xs text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full hover:bg-teal-100 transition-colors"
            >
              PMID:{c.pmid}
            </a>
          ))}
        </div>
      )}
      {sanitized && (
        <p className="text-[10px] text-amber-600 mt-1" data-testid="litscholar-citation-warning">
          {warning || 'Some citations were removed for accuracy.'}
        </p>
      )}
    </>
  );

  const renderDisclaimer = (disclaimer) =>
    disclaimer && <p className="text-[10px] text-slate-400 italic mt-2">{disclaimer}</p>;

  const renderProfileBadge = () => {
    if (profileLoading) {
      return (
        <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="litscholar-profile-loading">
          <RefreshCw className="h-3 w-3 animate-spin" />
          Loading your expertise profile...
        </div>
      );
    }
    if (!profile || (!profile.specialty_ids?.length && !Object.keys(profile.topic_weights || {}).length)) {
      return null;
    }
    const topTopics = Object.entries(profile.topic_weights || {})
      .sort(([, a], [, b]) => b - a)
      .slice(0, 4)
      .map(([k]) => k);
    return (
      <div className="bg-teal-50/60 border border-teal-200 rounded-lg p-2.5 space-y-1" data-testid="litscholar-profile-badge">
        <p className="text-[10px] font-semibold text-teal-700 uppercase tracking-wider flex items-center gap-1">
          <User className="h-3 w-3" /> Your Expertise Context
        </p>
        {profile.specialty_ids?.length > 0 && (
          <p className="text-xs text-slate-600">{profile.specialty_ids.join(', ')}</p>
        )}
        {topTopics.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {topTopics.map((t) => (
              <span key={t} className="text-[10px] bg-teal-100 text-teal-800 px-1.5 py-0.5 rounded">
                {t}
              </span>
            ))}
          </div>
        )}
        <p className="text-[10px] text-slate-400 italic">Responses are personalized to your reading patterns.</p>
      </div>
    );
  };

  const handleSaveArtifact = async (type, title, summaryText, artifactPmids) => {
    try {
      await litScholarAPI.saveArtifact({
        artifact_type: type,
        title: title || 'Untitled',
        summary_text: summaryText || '',
        pmids: artifactPmids || [],
      });
      toast.success('Saved to Lit Scholar artifacts');
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error_code === 'phi_detected') toast.error('Cannot save: PHI detected in content.');
      else if (d?.error_code === 'artifact_limit_reached') toast.error(d.message || 'Artifact limit reached.');
      else toast.error('Failed to save artifact.');
    }
  };

  const renderIdle = () => (
    <div className="space-y-4 mt-2" data-testid="litscholar-prompts">
      <p className="text-xs text-slate-500">
        No patient information. Lit Scholar is for literature discussion only.
      </p>

      {/* Expertise profile context */}
      {renderProfileBadge()}

      {isMultiMode ? (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <p className="text-xs font-medium text-slate-600 mb-2">
            {selectedArticles.length} article{selectedArticles.length !== 1 ? 's' : ''} selected
          </p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {selectedArticles.map((a) => (
              <p key={a.pmid} className="responsive-title text-xs text-slate-500 truncate">
                {a.title}
              </p>
            ))}
          </div>
        </div>
      ) : article ? (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <p className="text-xs font-medium text-slate-600">Current article</p>
          <p className="responsive-title text-xs text-slate-500 mt-1 line-clamp-2">{article.title}</p>
        </div>
      ) : null}

      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
          What would you like to do?
        </p>
        {prompts.map((p) => {
          const Icon = p.icon;
          return (
            <button
              key={p.id}
              onClick={p.action}
              disabled={p.disabled || !!quotaError}
              className="w-full flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:bg-teal-50 hover:border-teal-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-left group"
              data-testid={`litscholar-prompt-${p.id}`}
            >
              <Icon className="h-5 w-5 text-teal-600 mt-0.5 shrink-0 group-hover:text-teal-700" />
              <div>
                <p className="text-sm font-medium text-slate-800 group-hover:text-teal-800">
                  {p.label}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>
                {p.id === 'compare_studies' && !canCompare && pmids.length > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {pmids.length < 2 ? 'Select at least 2 articles' : 'Maximum 5 articles'}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {renderQuotaWarning()}
    </div>
  );

  const renderEvidenceBrief = () => (
    <div className="space-y-4 mt-2" data-testid="litscholar-evidence-brief">
      <button
        onClick={() => setSurface('idle')}
        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
        data-testid="litscholar-back-btn"
      >
        <ArrowLeft className="h-3 w-3" /> Back to prompts
      </button>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
          <span className="ml-2 text-sm text-slate-600">Generating evidence brief...</span>
        </div>
      ) : briefResult ? (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-3">
          <h4 className="font-semibold text-sm text-teal-900 flex items-center gap-2">
            <FileText className="h-4 w-4" /> Evidence Brief
          </h4>
          {briefResult.cached && (
            <Badge variant="secondary" className="text-[10px]">cached</Badge>
          )}
          {briefResult.evidence_brief?.clinical_bottom_line && (
            <p className="text-sm font-medium text-slate-900">
              {briefResult.evidence_brief.clinical_bottom_line}
            </p>
          )}
          {briefResult.evidence_brief?.summary && (
            <p className="text-sm text-slate-700">{briefResult.evidence_brief.summary}</p>
          )}
          {briefResult.evidence_brief?.key_findings?.length > 0 && (
            <div>
              <span className="text-xs font-medium text-slate-500">Key Findings:</span>
              <ul className="text-sm text-slate-600 ml-3 mt-1 space-y-1">
                {briefResult.evidence_brief.key_findings.map((f, i) => (
                  <li key={i}>&bull; {f}</li>
                ))}
              </ul>
            </div>
          )}
          {briefResult.evidence_brief?.limitations?.length > 0 && (
            <div>
              <span className="text-xs font-medium text-slate-500">Limitations:</span>
              <ul className="text-sm text-slate-600 ml-3 mt-1 space-y-1">
                {briefResult.evidence_brief.limitations.map((l, i) => (
                  <li key={i}>&bull; {l}</li>
                ))}
              </ul>
            </div>
          )}
          {renderCitations(briefResult.citations, briefResult.citations_sanitized, briefResult.citation_warning)}
          {renderDisclaimer(briefResult.disclaimer)}
          <Button
            variant="outline"
            size="sm"
            className="mt-2 text-xs"
            onClick={() => handleSaveArtifact(
              'evidence_brief',
              briefResult.title || briefResult.evidence_brief?.clinical_bottom_line || 'Evidence Brief',
              briefResult.evidence_brief?.summary || briefResult.evidence_brief?.clinical_bottom_line || '',
              pmids
            )}
            data-testid="litscholar-save-brief-btn"
          >
            <Bookmark className="h-3 w-3 mr-1" /> Save to Artifacts
          </Button>
        </div>
      ) : null}

      {renderQuotaWarning()}
    </div>
  );

  const renderAskArticle = () => (
    <div className="space-y-4 mt-2" data-testid="litscholar-ask-article">
      <button
        onClick={() => { setSurface('idle'); setAnswerResult(null); }}
        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
        data-testid="litscholar-back-btn"
      >
        <ArrowLeft className="h-3 w-3" /> Back to prompts
      </button>

      <div className="space-y-2">
        <p className="text-xs text-slate-500">
          Ask a question grounded in the article's abstract. No patient information.
        </p>
        <div className="flex gap-2">
          <input
            className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
            placeholder="e.g., What was the primary endpoint?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAskArticle()}
            disabled={!!quotaError || loading}
            data-testid="litscholar-question-input"
          />
          <Button
            size="sm"
            onClick={handleAskArticle}
            disabled={loading || !question.trim() || !!quotaError}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="litscholar-ask-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {answerResult && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2" data-testid="litscholar-answer">
          <p className="text-sm text-slate-800">{answerResult.answer}</p>
          {answerResult.confidence && (
            <Badge variant="secondary" className="text-[10px]">
              Confidence: {answerResult.confidence}
            </Badge>
          )}
          {answerResult.what_to_check_in_full_text?.length > 0 && (
            <p className="text-xs text-teal-600">
              Check in full text: {answerResult.what_to_check_in_full_text.join(', ')}
            </p>
          )}
          {renderCitations(answerResult.citations, answerResult.citations_sanitized, answerResult.citation_warning)}
          {renderDisclaimer(answerResult.disclaimer)}
          <Button
            variant="outline"
            size="sm"
            className="mt-2 text-xs"
            onClick={() => handleSaveArtifact(
              'ask_answer',
              `Q&A: ${(answerResult.answer || '').slice(0, 80)}`,
              answerResult.answer || '',
              pmids
            )}
            data-testid="litscholar-save-answer-btn"
          >
            <Bookmark className="h-3 w-3 mr-1" /> Save to Artifacts
          </Button>
        </div>
      )}

      {renderQuotaWarning()}
    </div>
  );

  const renderCompareStudies = () => (
    <div className="space-y-4 mt-2" data-testid="litscholar-compare-studies">
      <button
        onClick={() => { setSurface('idle'); setCompareResult(null); }}
        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
        data-testid="litscholar-back-btn"
      >
        <ArrowLeft className="h-3 w-3" /> Back to prompts
      </button>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
          <span className="ml-2 text-sm text-slate-600">Comparing {pmids.length} studies...</span>
        </div>
      ) : compareResult ? (
        <div className="space-y-3">
          <h4 className="font-semibold text-sm text-teal-900 flex items-center gap-2">
            <GitCompareArrows className="h-4 w-4" />
            {compareResult.comparison_title || 'Study Comparison'}
          </h4>
          {compareResult.cached && (
            <Badge variant="secondary" className="text-[10px]">cached</Badge>
          )}
          {compareResult.table && (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr>
                    {(compareResult.table.columns || []).map((c, i) => (
                      <th
                        key={i}
                        className="border-b border-slate-200 px-3 py-2 bg-slate-50 text-left font-medium text-slate-600"
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(compareResult.table.rows || []).map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      {row.map((cell, j) => (
                        <td key={j} className="border-b border-slate-100 px-3 py-2 text-slate-700">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {compareResult.synthesis && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
              <p className="text-sm text-teal-900">{compareResult.synthesis}</p>
            </div>
          )}
          {renderCitations(compareResult.citations, compareResult.citations_sanitized, compareResult.citation_warning)}
          {renderDisclaimer(compareResult.disclaimer)}
          <Button
            variant="outline"
            size="sm"
            className="mt-2 text-xs"
            onClick={() => handleSaveArtifact(
              'comparison',
              compareResult.comparison_title || 'Study Comparison',
              compareResult.synthesis || '',
              pmids
            )}
            data-testid="litscholar-save-compare-btn"
          >
            <Bookmark className="h-3 w-3 mr-1" /> Save to Artifacts
          </Button>
        </div>
      ) : null}

      {renderQuotaWarning()}
    </div>
  );

  const renderDraftPost = () => (
    <div className="space-y-4 mt-2" data-testid="litscholar-draft-post">
      <button
        onClick={() => { setSurface('idle'); setDraftResult(null); }}
        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
        data-testid="litscholar-back-btn"
      >
        <ArrowLeft className="h-3 w-3" /> Back to prompts
      </button>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
          <span className="ml-2 text-sm text-slate-600">Drafting discussion post...</span>
        </div>
      ) : draftResult ? (
        <div className="space-y-3">
          <h4 className="font-semibold text-sm text-teal-900 flex items-center gap-2">
            <PenLine className="h-4 w-4" /> Draft Discussion Post
          </h4>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="litscholar-draft-text">
              {draftResult.draft_post}
            </p>
          </div>
          {draftResult.suggested_questions?.length > 0 && (
            <div>
              <span className="text-xs font-medium text-slate-500">Suggested questions:</span>
              <ul className="text-sm text-slate-600 ml-3 mt-1 space-y-1">
                {draftResult.suggested_questions.map((q, i) => (
                  <li key={i}>&bull; {q}</li>
                ))}
              </ul>
            </div>
          )}
          {renderCitations(draftResult.citations, draftResult.citations_sanitized, draftResult.citation_warning)}
          {renderDisclaimer(draftResult.disclaimer)}
          <div className="flex gap-2 pt-2 border-t border-slate-200">
            <Button
              size="sm"
              className="bg-teal-600 hover:bg-teal-700 text-white"
              onClick={handleOpenInCommunity}
              data-testid="litscholar-open-in-community-btn"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Open in Community Composer
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => {
                navigator.clipboard.writeText(draftResult.draft_post || '');
                toast.success('Draft copied to clipboard');
              }}
              data-testid="litscholar-copy-draft-btn"
            >
              Copy Draft
            </Button>
          </div>
        </div>
      ) : null}

      {renderQuotaWarning()}
    </div>
  );

  const surfaceRenderers = {
    idle: renderIdle,
    evidence_brief: renderEvidenceBrief,
    ask_article: renderAskArticle,
    compare_studies: renderCompareStudies,
    draft_post: renderDraftPost,
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        if (!v) resetState();
        onOpenChange(v);
      }}
    >
      <SheetContent
        side="right"
        className="w-full sm:max-w-md md:max-w-lg overflow-y-auto"
        data-testid="litscholar-panel"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-teal-800">
            <BookOpen className="h-5 w-5" />
            Lit Scholar
          </SheetTitle>
          <SheetDescription>
            AI-powered literature analysis grounded in article abstracts.
          </SheetDescription>
        </SheetHeader>

        {surfaceRenderers[surface]()}
      </SheetContent>
    </Sheet>
  );
};
